import api from "./api";

// CAPI (Authority / Power / Influence) — one record per job.
// Same shape as PAEI: no "find by job_id" route, and `findAll` returns a plain
// `{ message }` object instead of an array when access is denied, so treat
// anything non-array as "nothing found" rather than an error.
const extractCapiArray = (payload) => (Array.isArray(payload) ? payload : []);

export const normalizeCapi = (c) => {
  if (!c || typeof c !== "object") return null;
  return {
    id: c.id,
    job_id: c.job_id,
    authority: Number(c.authority) || 0,
    power: Number(c.power) || 0,
    influence: Number(c.influence) || 0,
    createdBy: c.createdBy ?? null,
    updatedBy: c.updatedBy ?? null,
    createdAt: c.createdAt ?? null,
    updatedAt: c.updatedAt ?? null,
  };
};

export const listCapi = async () => {
  const res = await api.get("/capi");
  return extractCapiArray(res).map(normalizeCapi).filter(Boolean);
};

export const getCapiByJobId = async (jobId) => {
  if (!jobId) return null;
  try {
    const all = await listCapi();
    return all.find((c) => String(c.job_id) === String(jobId)) || null;
  } catch {
    return null;
  }
};

export const createCapi = async (payload) => {
  const body = {
    job_id: Number(payload.job_id),
    authority: Number(payload.authority) || 0,
    power: Number(payload.power) || 0,
    influence: Number(payload.influence) || 0,
  };
  const res = await api.post("/capi", body);
  return normalizeCapi(res);
};

export const updateCapi = async (id, payload) => {
  if (!id) throw new Error("CAPI record id is required");
  const body = {
    authority: Number(payload.authority) || 0,
    power: Number(payload.power) || 0,
    influence: Number(payload.influence) || 0,
  };
  const res = await api.patch(`/capi/${id}`, body);
  return normalizeCapi(res);
};

// Creates when no record exists yet for the job, otherwise updates in place.
// Unlike PAEI, the backend doesn't guard CAPI create against duplicates with a
// 409 — self-heal the same way regardless, in case a unique-constraint error
// surfaces some other way (e.g. a raw DB error).
export const saveCapiForJob = async (jobId, values, existingId) => {
  if (existingId) {
    return updateCapi(existingId, values);
  }
  try {
    return await createCapi({ job_id: jobId, ...values });
  } catch (e) {
    if (Number(e?.status) === 409) {
      const existing = await getCapiByJobId(jobId);
      if (existing?.id) return updateCapi(existing.id, values);
    }
    throw e;
  }
};

export const deleteCapi = async (id) => {
  if (!id) throw new Error("CAPI record id is required");
  return api.delete(`/capi/${id}`);
};
